import React, { useEffect, useState } from 'react'
import { Alert } from '@livingdesign/react';
import "./CSVUploadOverlay.scss";
import { Breadcrumb, BreadcrumbItem } from '@walmart-web/livingdesign-components';
import CSVUploadContainer from './CSVUploadContainer';
import { CAMPAIGN_CSV_UPLOAD_CONFIRGURATIONS, UI_DATA } from '../../../../../../../consts/Campaign';
import { useMessageDetailsForm } from '../../../../../../../contexts/MessageDetailsForm';
import EditFormActionsFooter from '../../EditFormActionsFooter';
import CSVToFieldSelectionContainer from './CSVToFieldSelectionContainer';
import { useCampaignContext } from '../../../../../../../contexts/Campaign';
import _ from 'lodash';
import CampaignEditCsvUpload from '../../../../../../campaign/CampaignEditCsvUpload';
import { CAMPAIGN, RECIPIENTS } from '../../../../../../../consts/DBFields';

export const QA_ID_PREFIX = "CSVUploadOverlay";

function CSVUploadOverlay({
    open,
    handleClose,
    formValues,
}) {

    const {
        savingCampaign,
    } = useCampaignContext();

    const {
        saveCampaign,
        selectedCampaign,
        uploadPageLoading,
        setError,
    } = useMessageDetailsForm();

    const [currentStep, setCurrentStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [uploaderError, setUploaderError] = useState(null);

    useEffect(() => {
        if(!open) return;
        setError("csvFiles", null);
        setError("to", null);
        setUploaderError(null);
        const steps = [
            {
                title: "Upload CSV Files",
                component: <CSVUploadContainer
                                csvFiles={formValues.csvFiles}
                                accept={CAMPAIGN_CSV_UPLOAD_CONFIRGURATIONS.accept}
                                maxFiles={CAMPAIGN_CSV_UPLOAD_CONFIRGURATIONS.maxFiles}
                                sizeLimit={CAMPAIGN_CSV_UPLOAD_CONFIRGURATIONS.sizeLimit}
                                multiple={CAMPAIGN_CSV_UPLOAD_CONFIRGURATIONS.multiple}
                                onError={setUploaderError}
                           />
            }
        ];
        if(!selectedCampaign?.[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]) {
            steps.push({
                title: "Select your data (header)",
                component: <CSVToFieldSelectionContainer />
            })
        }
        setSteps(steps);
        setCurrentStep(0);
    }, [open]);

    if(!open) return null;

    const validateCSVUploadStep = (stepIndex, formValues) => {
        switch(stepIndex) {
            case 0: // Validate CSV file upload
                // Check if any CSV files are uploaded
                return !_.isEmpty(formValues.csvFiles);
            case 1: // Validate field mapping
                // Check if all CSV files have a corresponding 'To' field selected
                if(!formValues.csvFiles || formValues.csvFiles.length <= 0) {
                    return false;
                }
                if(!_.isEmpty(formValues.to)) {
                    const selectedToField = formValues.to;
                    if(!selectedToField || selectedToField === UI_DATA.MANUAL_RECIPIENTS[RECIPIENTS.TO]) {
                        return false;
                    }
                }
                return true;
            default:
                return false;
        }
    }

    const saveAndClose = async () => {
        if(savingCampaign) return;
        await saveCampaign(selectedCampaign);
        handleClose();
    }

    return (
        <main className='csv-upload-overlay' data-testid={`${QA_ID_PREFIX}-main`}>
            <div className='csv-upload-main' data-testid={`${QA_ID_PREFIX}-container`}>
                <Breadcrumb data-testid={`${QA_ID_PREFIX}-breadcrumb`}>
                    <BreadcrumbItem data-testid={`${QA_ID_PREFIX}-breadcrumb-home`}>Home</BreadcrumbItem>
                    <BreadcrumbItem
                        onClick={ async () => {
                            await saveAndClose();
                        }}
                        data-testid={`${QA_ID_PREFIX}-breadcrumb-create-message`}
                        UNSAFE_style={{cursor: 'pointer'}}
                    >
                        Create new message
                    </BreadcrumbItem>
                    <BreadcrumbItem data-testid={`${QA_ID_PREFIX}-breadcrumb-csv-upload`}>CSV Upload</BreadcrumbItem>
                </Breadcrumb>
                {uploaderError &&
                    <Alert
                        variant="error"
                        data-testid={`${QA_ID_PREFIX}-uploader-error`}
                        UNSAFE_style={{ marginTop: "12px", marginBottom: "12px" }}
                    >
                        {uploaderError.isSizeError
                            ? `File exceeds the upload limit${uploaderError.sizeLimitMB ? ` of ${uploaderError.sizeLimitMB} MB` : ''}. Please upload a smaller file.`
                            : uploaderError.message}
                    </Alert>
                }
                {steps.length > 0 &&
                    <div data-testid={`${QA_ID_PREFIX}-steps-container`}>
                        <section className='csv-upload-title' data-testid={`${QA_ID_PREFIX}-title-section`}>
                            <h2 data-testid={`${QA_ID_PREFIX}-step-title`}>{steps[currentStep].title}</h2>
                            <p data-testid={`${QA_ID_PREFIX}-step-description`}>The supporting body text should be clear, meaningful, and helpful, guiding users to understand what actions they can take.</p>
                        </section>
                        <section data-testid={`${QA_ID_PREFIX}-step-content`}>
                            {steps?.[currentStep] && steps[currentStep].component}
                        </section>
                    </div>
                }
                <section data-testid={`${QA_ID_PREFIX}-actions-section`}>
                    <EditFormActionsFooter
                        loading={uploadPageLoading || savingCampaign}
                        disableContinue={!validateCSVUploadStep(currentStep, formValues)}
                        onPrevious={ async () => {
                            if(currentStep > 0) {
                                setCurrentStep(currentStep - 1);
                            } else {
                                await saveAndClose();
                            }
                        }}
                        onSaveDraft={() => saveCampaign(selectedCampaign)}
                        onContinue={ async () => {
                            if(currentStep < steps.length - 1) {
                                setCurrentStep(currentStep + 1);
                                return;
                            } else {
                                await saveAndClose();
                            }
                        }}
                        data-testid={`${QA_ID_PREFIX}-actions-footer`}
                    />
                </section>
            </div>
        </main>
    )
}

export default CSVUploadOverlay;